import { createControlComponent } from "@react-leaflet/core";
import L, { Control } from "leaflet";

import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  MapContainer,
  Marker,
  Polygon,
  Popup,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

interface SideBarComponentProps {
  coordinates: any;
}

const redMarker = new L.Icon({
  iconUrl: require("../../../../assets/images/new-dashboard-icons/asset-details-map-icons/red-block.png"),
  iconSize: new L.Point(60, 53),
  className: "leaflet-div-icon",
});

const greenMarker = new L.Icon({
  iconUrl: require("../../../../assets/images/new-dashboard-icons/asset-details-map-icons/green-block.png"),
  iconSize: new L.Point(60, 53),
  className: "",
});

const yellowMarker = new L.Icon({
  iconUrl: require("../../../../assets/images/new-dashboard-icons/asset-details-map-icons/yellow-block.png"),
  iconSize: new L.Point(60, 53),
  className: "",
});

const ZoomEventListener = ({ setZoom }: any) => {
  useMapEvents({
    zoomend: (e) => {
      setZoom(e.target._zoom);
    },
  });
  return null;
};

export const ZoomControl = createControlComponent(
  (props) => new Control.Zoom(props)
);

// Utility component for zooming to marker
const ZoomToMarker = ({ center, zoom }: { center: any; zoom: number }) => {
  const map = useMapEvents({});
  useEffect(() => {
    if (center.length > 0) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map]);

  return null;
};

const AssetDetailMap: FC<SideBarComponentProps> = ({ coordinates }) => {
  const { lat, lng, healthStatusByLocationOrAsset, assetId } = coordinates;
  const healthStatus = healthStatusByLocationOrAsset[0];

  const [markers] = useState([[lat, lng]]);
  const [center, setCenter] = useState([] as any);
  const [polygon] = useState([]); // Complete this with relevant polygon data if available
  const [zoom, setZoom] = useState(12); // Default zoom level before zooming in

  const purpleOptions = { color: "purple" };

  const [dataLoaded, setDataLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mapHeight, setMapHeight] = useState<number>(652);

  useLayoutEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.height > 0) {
          setMapHeight(entry.contentRect.height);
        }
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      if (containerRef.current.clientHeight > 0) {
        setMapHeight(containerRef.current.clientHeight);
      }
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Effect to set the center and zoom in when the component loads
  useEffect(() => {
    if (coordinates) {
      setCenter([lat, lng]);
      setDataLoaded(true);
      setZoom(17); // Set zoom to focus on the marker
    }
  }, [coordinates]);

  return (
    <div
      ref={containerRef}
      style={{ height: "100%", width: "100%" }}
    >
      {dataLoaded ? (
        <MapContainer
          style={{
            height: `${mapHeight}px`,
            width: "100%",
            borderRadius: "5px",
          }}
          attributionControl={false}
          center={center as any}
          zoom={zoom}
          zoomControl={false}
          scrollWheelZoom={true}
          minZoom={12}
          maxZoom={19}
          maxBounds={[
            [1.21092, 103.60565],
            [1.47046, 104.04192],
          ]}
        >
          <ZoomControl position="topright" />

          <TileLayer
            maxZoom={19}
            maxNativeZoom={18}
            detectRetina={true}
            attribution='<img src="https://www.onemap.gov.sg/web-assets/images/logo/om_logo.png" style="height:20px;width:20px;"/>&nbsp;<a href="https://www.onemap.gov.sg/" target="_blank" rel="noopener noreferrer">OneMap</a>&nbsp;&copy;&nbsp;contributors&nbsp;&#124;&nbsp;<a href="https://www.sla.gov.sg/" target="_blank" rel="noopener noreferrer">Singapore Land Authority</a>'
            url="https://www.onemap.gov.sg/maps/tiles/Default_HD/{z}/{x}/{y}.png"
          />

          {/* Zoom to marker when map loads */}
          <ZoomToMarker center={center} zoom={zoom} />

          {markers.map((marker: any, index: number) => (
            <Marker
              key={index}
              icon={
                healthStatus?.gatewayFailure == 0 &&
                  healthStatus?.lightFailure == 0 &&
                  healthStatus?.networkFailure == 0 &&
                  healthStatus?.sensorFailure == 0
                  ? greenMarker
                  : healthStatus?.lightFailure ||
                    healthStatus?.networkFailure ||
                    healthStatus?.sensorFailure
                    ? yellowMarker
                    : redMarker
              }
              position={marker}
              eventHandlers={{
                click: () => {
                  setCenter(marker); // Update center to zoom to the marker
                  setZoom(18); // Zoom in closer when the marker is clicked
                },
                mouseover: (e) => {
                  if (e.target && e.target.getPopup()) {
                    e.target.openPopup();
                  }
                },
                mouseout: (e) => {
                  if (e.target && e.target.getPopup()) {
                    e.target.closePopup();
                  }
                },
              }}
            >
              <Popup closeButton={false}>Asset ID: {assetId}</Popup>
            </Marker>
          ))}

          <Polygon pathOptions={purpleOptions} positions={polygon} />

          <ZoomEventListener setZoom={setZoom} />
        </MapContainer>
      ) : (
        ""
      )}
    </div>
  );
};

export default AssetDetailMap;
